import React, { useEffect, useRef, useImperativeHandle, forwardRef, useState } from "react";
import { Box, Grid } from "@mui/material";
import Sidebar from "app/components/flow/designer/sidebar";
import { componentID } from "core/services/componentService/component-service";
import { Node } from "reactflow";
import { sxStyles } from "./sidebar.styles";
import {
    getComponentMetadata,
    createAutoProperties,
    createMetaObject,
    createGraphNodeModel,
    attachSidebarEventListeners,
    renderSidebarView,
    cleanupSidebarView,
    SidebarEventHandlers
} from "./sidebar.utils";
import { FormTypes } from "../../flow/flow.constants";
import FormTitle from "./form-title";
import SidebarSkeleton from "./sidebar-skeleton";
import metaStoreService from "core/services/metaStoreService/meta-store-service";
import store from "src/stores/redux/store";
import { resetTestConnectionDataAction } from "src/stores/redux/features/sidebar/sidebar.slice";

type SidebarProps = {
    selectedNode: Node | null;
    flowId: string;
    app: any;
    appStatus: string | null;
    onSidebarSave?: (data: any) => void;
    onSidebarCancel?: () => void;
    onDelete?: () => void;
    onPreviewData?: (model: any) => void;
    onClearAndReload?: () => void;
    formType?: keyof typeof FormTypes;
    toggleFormType?: () => void;
};

export interface SidebarWrapperRef {
    isDirty: () => boolean;
}

const SidebarWrapper = forwardRef<SidebarWrapperRef, SidebarProps>(
    (
        {
            selectedNode,
            flowId,
            app,
            appStatus,
            onSidebarSave,
            onSidebarCancel,
            onDelete,
            onPreviewData,
            onClearAndReload,
            formType,
            toggleFormType
        },
        ref
    ) => {
        const containerRef = useRef<HTMLDivElement>(null);
        const sidebarViewRef = useRef<any>(null);
        const [isLoading, setIsLoading] = useState(false);

        // Expose isDirty method to parent component
        useImperativeHandle(ref, () => ({
            isDirty: () => !!sidebarViewRef.current?.isDirty?.()
        }));

        // Watch for app status changes and update the sidebar without recreating it
        useEffect(() => {
            if (sidebarViewRef.current && appStatus && app) {
                app.flowStatus = appStatus;
                sidebarViewRef.current.setAppStatus(appStatus);
            }
        }, [appStatus, app]);

        useEffect(() => {
            const renderSidebar = async () => {
                if (!selectedNode || !containerRef.current) {
                    return;
                }
                setIsLoading(true);

                // Get component metadata from the component service
                const componentId = selectedNode.data?.componentId;
                const { metadata: componentMetadata, adapter, parser } = getComponentMetadata(
                    componentId as componentID
                );

                // Only create auto properties for NEW components (not existing ones)
                // Existing components should load their saved data, not be prefilled with defaults
                const isNewComponent =
                    selectedNode.data?.metaObjectId === "temp-node" || !selectedNode.data?.metaObjectId;
                const clonedProperties = selectedNode.data?.clonedProperties;
                const autoProperties = clonedProperties
                    ? JSON.parse(JSON.stringify(clonedProperties)) // Deep clone to avoid mutating frozen Immer state
                    : isNewComponent
                    ? createAutoProperties(adapter, parser, componentId as componentID)
                    : {};

                // Create meta object from React Flow node
                const metaObject = createMetaObject(selectedNode, app, autoProperties);

                // Create Backbone GraphNodeModel
                const graphNodeModel = createGraphNodeModel(selectedNode, metaObject, componentMetadata, flowId, app);
                // fetch appMetaObject again before rendering
                const appMetaObject = await metaStoreService.findById(app.id);
                // Propagate runtime flowStatus so sidebar.js can correctly evaluate
                // preview/metadata button visibility (metadataVisibility/_shouldShowMetadataButton).
                // appMetaObject from metaStoreService is a stored object and does not carry flowStatus.
                if (appStatus) {
                    appMetaObject.flowStatus = appStatus;
                }

                // Create the Marionette Sidebar View with the GraphNodeModel
                const sidebarView = new Sidebar.View({
                    flowId: flowId,
                    app: appMetaObject,
                    model: graphNodeModel
                });

                // Attach event listeners to sidebar
                const eventHandlers: SidebarEventHandlers = {
                    onSidebarSave,
                    onSidebarCancel,
                    onDelete,
                    onPreviewData,
                    onClearAndReload
                };
                attachSidebarEventListeners(sidebarView, eventHandlers);

                // Hide loader once sidebar signals it has finished rendering the form
                sidebarView.on("sidebar-ready", () => setIsLoading(false));

                // Render the sidebar view
                renderSidebarView(sidebarView, containerRef.current);

                // Store reference for cleanup
                sidebarViewRef.current = sidebarView;
            };
            renderSidebar();
            // Cleanup function
            return () => {
                const viewToCleanup = sidebarViewRef.current;
                sidebarViewRef.current = null;
                setIsLoading(false);
                store.dispatch(resetTestConnectionDataAction());
                // Defer destroy to avoid synchronously unmounting React roots embedded
                // in the Marionette sidebar while React is still in its commit phase.
                setTimeout(() => {
                    cleanupSidebarView(viewToCleanup);
                }, 0);
            };
        }, [selectedNode, flowId, app?.id]); // Only recreate when node, flow, or app ID changes, not when app status changes

        if (!app) return null;

        if (formType === FormTypes.MODAL) return <Box ref={containerRef} sx={sxStyles.sidebar(formType)} />;
        else
            return (
                <Grid height={"calc(100% - 70px)"} sx={{ backgroundColor: "white", position: "relative" }}>
                    <FormTitle node={selectedNode} type={formType} toggleFormType={toggleFormType} />
                    {isLoading && (
                        <Box sx={{ borderLeft: "1px solid #e0e0e0" }}>
                            <SidebarSkeleton />
                        </Box>
                    )}
                    <Box
                        ref={containerRef}
                        sx={{
                            ...sxStyles.sidebar(formType ?? "SIDEBAR"),
                            visibility: isLoading ? "hidden" : "visible"
                        }}
                    />
                </Grid>
            );
    }
);

export default SidebarWrapper;
